import firebase from "firebase/app";
import { getUploadedFile } from "src/utils/storage";
import type {
  BalanceSheetReport,
  InventoryReport,
  InventoryReportData,
} from "src/pages/ReportsPage/report";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import type { Sale } from "src/interfaces/sale.interface";

/** Payload from `reports-getScheduleCComputation` (dates restored via `parseSaleWire`). */
export interface ScheduleCComputationPayload {
  year: number;
  mappingsByExpenseType: Record<string, string[]>;
  groupingsByAccountName: Record<
    string,
    { id: string; name: string; group: string }
  >;
  accountNamesById: Record<string, string>;
  inventoryType: "accrual" | "cash";
  sales: Sale[];
  refunds: Sale[];
  transactions: Array<{
    id: string;
    gl_account_id: string;
    amount: number;
    account?: string;
    description?: string;
  }>;
  inventoryReport: {
    beginning: { amount: number };
    during: { amount: number };
    end: { amount: number };
    cost: { amount: number };
  };
  incomeAccounts: { discount: string[]; nonDiscount: string[] };
  companyName?: string;
}

function parseDateField(v: unknown): Date {
  if (v instanceof Date) return v;
  if (v == null) return new Date(NaN);
  return new Date(String(v));
}

/** Restore `Date` instances after JSON from httpsCallable. */
function parseBalanceSheetReportWire(data: unknown): BalanceSheetReport {
  const d = data as BalanceSheetReport;
  const entries = d.entries.map((group) => ({
    ...group,
    children: group.children.map((section) => ({
      ...section,
      children: section.children.map((entry) => ({
        ...entry,
        transactions: (entry.transactions || []).map((t) => {
          const mt = t as MappedTransaction;
          return {
            ...mt,
            date: parseDateField(mt.date as unknown),
          };
        }),
      })),
    })),
  }));
  return {
    type: "balance-sheet",
    parameters: {
      date: parseDateField(d.parameters?.date as unknown),
    },
    entries,
  };
}

function isReportDocumentPathResponse(
  data: unknown
): data is { documentPath: string } {
  if (data == null || typeof data !== "object") return false;
  const p = (data as { documentPath?: unknown }).documentPath;
  return typeof p === "string" && p.length > 0;
}

async function resolveReportData(data: unknown): Promise<unknown> {
  if (!isReportDocumentPathResponse(data)) return data;
  const url = await getUploadedFile(data.documentPath);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load report file (${response.status})`);
  }
  return await response.json();
}

export async function fetchBalanceSheetReport(
  dateYmd: string
): Promise<BalanceSheetReport> {
  const fn = firebase
    .functions()
    .httpsCallable("reports-getBalanceSheetReport", { timeout: 120000 });
  const res = await fn({ date: dateYmd });
  const data = await resolveReportData(res.data);
  return parseBalanceSheetReportWire(data);
}

function parseSaleWire(s: Sale): Sale {
  return {
    ...s,
    sale_date:
      s.sale_date != null
        ? parseDateField(s.sale_date as unknown)
        : s.sale_date,
    purchase_date:
      s.purchase_date != null
        ? parseDateField(s.purchase_date as unknown)
        : s.purchase_date,
    return_date:
      s.return_date != null
        ? parseDateField(s.return_date as unknown)
        : s.return_date,
    list_date:
      s.list_date != null
        ? parseDateField(s.list_date as unknown)
        : s.list_date,
  };
}

function parseInventoryReportWire(wire: unknown): InventoryReport {
  const d = wire as InventoryReport;
  const parseLine = (line: {
    amount: number;
    transactions: MappedTransaction[];
  }) => ({
    amount: line.amount,
    transactions: (line.transactions || []).map((t) => ({
      ...t,
      date: parseDateField(t.date as unknown),
    })),
  });
  const rd = d.data;
  const empty: InventoryReportData = {
    beginning: { amount: 0, transactions: [] },
    during: { amount: 0, transactions: [] },
    end: { amount: 0, transactions: [] },
    cost: { amount: 0, transactions: [] },
    invalidPurchaseDateSales: [],
  };
  const reportData: InventoryReportData = rd
    ? {
        beginning: parseLine(rd.beginning),
        during: parseLine(rd.during),
        end: parseLine(rd.end),
        cost: parseLine(rd.cost),
        invalidPurchaseDateSales: (rd.invalidPurchaseDateSales || []).map((s) =>
          parseSaleWire(s as Sale)
        ),
      }
    : empty;
  return {
    type: "inventory",
    start: parseDateField(d.start as unknown),
    end: parseDateField(d.end as unknown),
    data: reportData,
    year: d.year,
  };
}

export async function fetchInventoryReport(
  year: number
): Promise<InventoryReport> {
  const fn = firebase
    .functions()
    .httpsCallable("reports-getInventoryReport", { timeout: 120000 });
  const res = await fn({ year });
  const data = await resolveReportData(res.data);
  return parseInventoryReportWire(data);
}

export async function fetchScheduleCComputation(
  year: number
): Promise<ScheduleCComputationPayload> {
  const fn = firebase
    .functions()
    .httpsCallable("reports-getScheduleCComputation", { timeout: 120000 });
  const res = await fn({ year });
  const data = await resolveReportData(res.data);
  const d = data as ScheduleCComputationPayload & {
    sales: Sale[];
    refunds: Sale[];
  };
  return {
    ...d,
    sales: (d.sales || []).map((s) => parseSaleWire(s)),
    refunds: (d.refunds || []).map((s) => parseSaleWire(s)),
  };
}
